import React, { useEffect, useRef, useState } from "react";
import ChairManager from "./components/ChairManager";
import Dashboard from "./components/Dashboard";
import StudentEnquiryForm from "./components/StudentEnquiryForm";
import StudentRegistration from "./components/StudentRegistration";
import BookRegistration from "./components/BookRegistration";

import StudentList from "./components/StudentList";
import SeatAllotment from "./components/SeatAllotment";
import ReceiptGenerator from "./components/ReceiptGenerator";
import Login from "./components/Login";
import { EnvironmentProvider } from "./contexts/EnvironmentContext";
import { AuthProvider } from "./contexts/AuthContext";
import { useEnvironment } from "./contexts/EnvironmentContext";
import { useAuth } from "./contexts/AuthContext";
import "./styles/App.css";
import StudentPayments from "./components/StudentPayments";

const AppContent: React.FC = () => {
  const { isAdmin, switchEnvironment } = useEnvironment();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [activeView, setActiveView] = useState<
    | "dashboard"
    | "seating"
    | "enquiry"
    | "student-registration"
    | "student-list"
    | "seat-allotment"
    | "receipt"
    | "student-payments"
    | "book-registration"
    | "book-list"

  >("dashboard");
  const [studentMenuOpen, setStudentMenuOpen] = useState<boolean>(false);
    const [bookMenuOpen, setBookMenuOpen] = useState<boolean>(false);

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const environmentSetRef = useRef<boolean>(false);

  // Initialize sidebar state responsively and keep it in sync on resize
  useEffect(() => {
    const applyResponsiveSidebar = () => {
      const isMobile = window.innerWidth <= 768;
      setSidebarOpen(!isMobile);
    };
    applyResponsiveSidebar();
    window.addEventListener("resize", applyResponsiveSidebar);
    return () => window.removeEventListener("resize", applyResponsiveSidebar);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [userMenuOpen]);

  // Set environment based on user role when user is authenticated
  useEffect(() => {
    if (user && !environmentSetRef.current) {
      const userEnvironment: "admin" | "student" =
        user.role === "admin" ? "admin" : "student";
      switchEnvironment(userEnvironment);
      environmentSetRef.current = true;
    }
  }, [user]);

  const isStudentViewActive = (view: string) => {
    return (
      view === "student-registration" ||
      view === "student-list" ||
      view === "seat-allotment" ||
      view === "student-payments"
    );
  };

   const isBookViewActive = (view: string) => {
    return (
      view === "book-registration" ||
      view === "book-list" 
     
    );
  };

    // Keep book menu open when a book view is active
  useEffect(() => {
    if (isBookViewActive(activeView)) {
      setBookMenuOpen(true);
    }
  }, [activeView]);

  // Keep student menu open when a student view is active
  useEffect(() => {
    if (isStudentViewActive(activeView)) {
      setStudentMenuOpen(true);
    }
  }, [activeView]);

  // Helper function to handle view change and close sidebar on mobile
  const handleViewChange = (
    view:
      | "dashboard"
      | "seating"
      | "enquiry"
      | "student-registration"
      | "student-list"
      | "seat-allotment"
      | "receipt"
      | "student-payments"
      | "book-registration"
      | "book-list"
  ) => {
    setActiveView(view);
    // Close sidebar on mobile after selecting an option
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  const renderContent = () => {
    if (activeView === "dashboard") return <Dashboard />;
    if (activeView === "seating") return <ChairManager />;
    if (activeView === "enquiry") return <StudentEnquiryForm />;
    if (activeView === "student-registration") return <StudentRegistration />;
    if (activeView === "student-list") return <StudentList />;
    if (activeView === "seat-allotment") return <SeatAllotment />;
    if (activeView === "receipt") return <ReceiptGenerator />;
    if (activeView === "student-payments") return <StudentPayments />;
        if (activeView === "book-registration") return <BookRegistration />;

    return <Dashboard />;
  };

  if (!isAdmin) {
    // Student view: only show seating arrangements, no sidebar
    return (
      <div className="App">
        <div className="topbar">
          <div className="topbar-title">Sitting Arrangements</div>
        </div>
        <main className="content with-topbar">
          <ChairManager />
        </main>
      </div>
    );
  }

  // Admin view: sidebar + navigable content
  return (
    <div className="App">
      <div className="topbar">
        <button
          className="sidebar-toggle mobile-only"
          aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {/* hamburger icon */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 6H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M3 12H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M3 18H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="topbar-title">
          {activeView === "dashboard" && "Dashboard"}
          {activeView === "seating" && "Sitting Arrangements"}
          {activeView === "enquiry" && "Student Enquiry"}
          {activeView === "student-registration" && "Student Registration"}
          {activeView === "student-list" && "Student List"}
          {activeView === "seat-allotment" && "Seat and RFID Allotment"}
          {activeView === "receipt" && "Receipt Generator"}
          {activeView === "student-payments" && "Student Payments"}
        </div>
        <div className="topbar-user" ref={userMenuRef}>
          <div className="avatar" aria-hidden="true">
            {user?.avatar || user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="user-meta">
            <div className="user-name">{user?.name || "User"}</div>
            <div className="user-role">
              {user?.role === "admin" ? "Administrator" : "Student"}
            </div>
          </div>
          <button
            className="user-menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label="Open user menu"
            title="User menu"
            onClick={() => setUserMenuOpen((v) => !v)}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {userMenuOpen && (
            <div className="user-dropdown" role="menu">
              <div className="dropdown-user-info">
                <div className="dropdown-user-name">{user?.name}</div>
                <div className="dropdown-user-email">{user?.email}</div>
              </div>
              <div className="dropdown-sep" />
              <button role="menuitem" className="dropdown-item">
                Profile
              </button>
              <button role="menuitem" className="dropdown-item">
                Settings
              </button>
              <div className="dropdown-sep" />
              <button
                role="menuitem"
                className="dropdown-item danger"
                onClick={logout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={`layout`}>
        <aside className={`sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
          <div className="sidebar-header">
            <div className="sidebar-title">
              <span className="sidebar-title-text">Library Admin</span>
            </div>
            <button
              className="sidebar-toggle"
              aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
          <nav className="sidebar-nav">
            <button
              className={`nav-item ${
                activeView === "dashboard" ? "active" : ""
              }`}
              onClick={() => handleViewChange("dashboard")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 12L12 3L21 12V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 21V12H15V21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Dashboard</span>
            </button>

            <div className="nav-group">
              <button
                className={`nav-item nav-parent ${
                  isStudentViewActive(activeView) ? "active" : ""
                }`}
                onClick={() => {
                  setStudentMenuOpen(!studentMenuOpen);
                }}
              >
                <span className="nav-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="9"
                      cy="7"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="nav-label">Student</span>
                <span className={`nav-arrow ${studentMenuOpen ? "open" : ""}`}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              {studentMenuOpen && (
                <div className="nav-submenu">
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "student-registration" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("student-registration")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="9"
                          cy="7"
                          r="4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Student Registration</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "student-list" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("student-list")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 11l3 3L22 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Student List</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "seat-allotment" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("seat-allotment")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 10H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6 14H18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="16"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Seat and RFID Allotment</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "student-payments" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("student-payments")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 10H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6 14H18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="16"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Student Payments</span>
                  </button>
                </div>
              )}
            </div>



            <div className="nav-group">
              <button
                className={`nav-item nav-parent ${
                  isBookViewActive(activeView) ? "active" : ""
                }`}
                onClick={() => {
                  setBookMenuOpen(!bookMenuOpen);
                }}
              >
                <span className="nav-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="9"
                      cy="7"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="nav-label">Book</span>
                <span className={`nav-arrow ${bookMenuOpen ? "open" : ""}`}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              {bookMenuOpen && (
                <div className="nav-submenu">
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "book-registration" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("book-registration")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="9"
                          cy="7"
                          r="4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Book Registration</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "student-list" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("book-list")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 11l3 3L22 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Book List</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "seat-allotment" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("seat-allotment")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 10H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6 14H18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="16"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Seat and RFID Allotment</span>
                  </button>
                  <button
                    className={`nav-item nav-subitem ${
                      activeView === "student-payments" ? "active" : ""
                    }`}
                    onClick={() => handleViewChange("student-payments")}
                  >
                    <span className="nav-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 10H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6 14H18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="16"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </span>
                    <span className="nav-label">Student Payments</span>
                  </button>
                </div>
              )}
            </div>
            <button
              className={`nav-item ${activeView === "seating" ? "active" : ""}`}
              onClick={() => handleViewChange("seating")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M6 14H18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </span>
              <span className="nav-label">Sitting Arrangements</span>
            </button>
            <button
              className={`nav-item ${activeView === "enquiry" ? "active" : ""}`}
              onClick={() => handleViewChange("enquiry")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 15V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V15C3 16.1046 3.89543 17 5 17H16L21 21V15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Student Enquiry</span>
            </button>
            <button
              className={`nav-item ${activeView === "receipt" ? "active" : ""}`}
              onClick={() => handleViewChange("receipt")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="14 2 14 8 20 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="16"
                    y1="13"
                    x2="8"
                    y2="13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="16"
                    y1="17"
                    x2="8"
                    y2="17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="10 9 9 9 8 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Receipt Generator</span>
            </button>
          </nav>
        </aside>
        <div
          className={`sidebar-backdrop ${sidebarOpen ? "show" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />
        <main className="content with-topbar">{renderContent()}</main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <EnvironmentProvider>
        <AppContent />
      </EnvironmentProvider>
    </AuthProvider>
  );
};

export default App;
